import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'
import './CustomerSetupDetail.css'

type LicenseInfo = {
  id: string
  license_key: string
  issued_at?: string
  expires_at?: string
  is_active: boolean
  days_remaining?: number
}

type CustomerSetupData = {
  id: string
  full_name: string
  email?: string
  phone?: string
  company_name?: string
  company_details?: string
  company_address?: string
  company_purpose?: string
  license_id?: string
  linked_user_id?: string
  license?: LicenseInfo
  created_at?: string
}

type LicenseItem = { id: string; license_key: string }

const PURPOSES = [
  { value: 'deliveries', label: 'משלוחים' },
  { value: 'agriculture', label: 'חקלאות' },
  { value: 'security', label: 'אבטחה' },
  { value: 'mapping', label: 'מיפוי' },
  { value: 'inspection', label: 'בדיקות' },
  { value: 'photography', label: 'צילום' },
  { value: 'other', label: 'אחר' },
]

const isNew = (id?: string) => !id || id === 'new'

function formatDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })
}

function CountdownTimer({ days }: { days?: number }) {
  if (days == null) return <span className="csd-no-expiry">אין תאריך תפוגה</span>
  if (days === 0) return <span className="csd-expired">פג תוקף</span>
  const cls = days <= 30 ? 'csd-warn' : days <= 90 ? 'csd-ok' : 'csd-good'
  return (
    <span className={`csd-countdown ${cls}`}>
      {days} ימים נותרו
    </span>
  )
}

export default function CustomerSetupDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const creating = isNew(id)

  const emptyForm = {
    full_name: '', email: '', phone: '',
    company_name: '', company_details: '', company_address: '',
    company_purpose: '', license_id: '', linked_user_id: '',
  }
  const [form, setForm] = useState(emptyForm)
  const [saveError, setSaveError] = useState('')
  const [saveSuccess, setSaveSuccess] = useState(false)

  const { data: record, isLoading, error } = useQuery<CustomerSetupData>({
    queryKey: ['customer-setup', id],
    queryFn: () => api.get(`/customer-setup/${id}`).then(r => r.data),
    enabled: !creating,
  })

  const { data: licenses = [] } = useQuery<LicenseItem[]>({
    queryKey: ['licenses'],
    queryFn: () => api.get('/licenses').then(r => r.data),
  })

  useEffect(() => {
    if (record) {
      setForm({
        full_name: record.full_name ?? '',
        email: record.email ?? '',
        phone: record.phone ?? '',
        company_name: record.company_name ?? '',
        company_details: record.company_details ?? '',
        company_address: record.company_address ?? '',
        company_purpose: record.company_purpose ?? '',
        license_id: record.license_id ?? '',
        linked_user_id: record.linked_user_id ?? '',
      })
    }
  }, [record])

  const createMutation = useMutation({
    mutationFn: (body: typeof emptyForm) => api.post('/customer-setup', body),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['customer-setup'] })
      navigate(`/settings/customer-setup/${res.data.id}`)
    },
    onError: (err: any) => setSaveError(err.response?.data?.detail || 'שגיאה ביצירה'),
  })

  const updateMutation = useMutation({
    mutationFn: (body: Partial<typeof emptyForm>) => api.put(`/customer-setup/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-setup', id] })
      queryClient.invalidateQueries({ queryKey: ['customer-setup'] })
      setSaveSuccess(true); setSaveError('')
      setTimeout(() => setSaveSuccess(false), 2500)
    },
    onError: (err: any) => { setSaveError(err.response?.data?.detail || 'שגיאה בשמירה'); setSaveSuccess(false) },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const payload = {
      ...form,
      license_id: form.license_id || null,
      linked_user_id: form.linked_user_id || null,
      company_purpose: form.company_purpose || null,
    }
    if (creating) createMutation.mutate(payload as any)
    else updateMutation.mutate(payload as any)
  }

  const set = (key: keyof typeof emptyForm, value: string) =>
    setForm(f => ({ ...f, [key]: value }))

  const isPending = createMutation.isPending || updateMutation.isPending
  const lic = record?.license

  if (!creating && isLoading) return <div className="page-loading">טוען...</div>
  if (!creating && (error || !record)) return (
    <div className="page-error">
      הרשומה לא נמצאה. <Link to="/settings/customer-setup">חזרה לרשימה</Link>
    </div>
  )

  return (
    <div className="csd-page">
      <div className="csd-header">
        <Link to="/settings/customer-setup" className="csd-back">← הגדרת לקוחות</Link>
        <h1>{creating ? 'לקוח חדש' : (form.full_name || 'עריכת לקוח')}</h1>
      </div>

      <div className="csd-body">
        <form onSubmit={handleSubmit} className="csd-form-wrap">

          {/* ─── Customer Details ─── */}
          <section className="csd-section panel">
            <h2>פרטי לקוח</h2>
            <div className="csd-grid">
              <label>
                שם מלא (חובה)
                <input value={form.full_name} onChange={e => set('full_name', e.target.value)} required placeholder="שם מלא" />
              </label>
              <label>
                אימייל
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@example.com" dir="ltr" />
              </label>
              <label>
                טלפון
                <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="טלפון" />
              </label>
            </div>
          </section>

          {/* ─── Company Details ─── */}
          <section className="csd-section panel">
            <h2>פרטי חברה</h2>
            <div className="csd-grid">
              <label>
                שם חברה
                <input value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="שם החברה" />
              </label>
              <label>
                תחום פעילות
                <select value={form.company_purpose} onChange={e => set('company_purpose', e.target.value)}>
                  <option value="">— בחר תחום —</option>
                  {PURPOSES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </label>
              <label className="csd-full">
                תיאור / פרטי חברה
                <textarea value={form.company_details} onChange={e => set('company_details', e.target.value)} rows={3} placeholder="תיאור החברה, מידע נוסף..." />
              </label>
              <label className="csd-full">
                כתובת חברה
                <input value={form.company_address} onChange={e => set('company_address', e.target.value)} placeholder="כתובת מלאה" />
              </label>
            </div>
          </section>

          {/* ─── Licensing ─── */}
          <section className="csd-section panel">
            <h2>מידע רישוי</h2>
            <div className="csd-grid">
              <label>
                קישור לרישיון (מהמערכת)
                <select value={form.license_id} onChange={e => set('license_id', e.target.value)}>
                  <option value="">— ללא רישיון —</option>
                  {licenses.map((l: any) => (
                    <option key={l.id} value={l.id}>{l.license_key}</option>
                  ))}
                </select>
              </label>
            </div>

            {/* License details (read-only, shown when editing) */}
            {!creating && lic && (
              <div className="csd-license-info">
                <div className="csd-license-row">
                  <span className="csd-license-label">מספר רישיון</span>
                  <span className="csd-license-value" dir="ltr">{lic.license_key}</span>
                </div>
                <div className="csd-license-row">
                  <span className="csd-license-label">תאריך הנפקה</span>
                  <span className="csd-license-value">{formatDate(lic.issued_at)}</span>
                </div>
                <div className="csd-license-row">
                  <span className="csd-license-label">תאריך תפוגה</span>
                  <span className="csd-license-value">{formatDate(lic.expires_at)}</span>
                </div>
                <div className="csd-license-row">
                  <span className="csd-license-label">זמן שנותר</span>
                  <CountdownTimer days={lic.days_remaining} />
                </div>
                <div className="csd-license-row">
                  <span className="csd-license-label">סטטוס</span>
                  <span className={`csd-license-status ${lic.is_active ? 'active' : 'inactive'}`}>
                    {lic.is_active ? 'פעיל' : 'לא פעיל'}
                  </span>
                </div>
              </div>
            )}

            {!creating && !lic && (
              <p className="csd-no-license">לא קושר רישיון. בחר רישיון מהרשימה למעלה ושמור.</p>
            )}
          </section>

          {saveError && <p className="form-error">{saveError}</p>}
          {saveSuccess && <p className="form-success">השינויים נשמרו בהצלחה</p>}

          <div className="form-actions">
            <button type="button" onClick={() => navigate('/settings/customer-setup')}>ביטול</button>
            <button type="submit" className="btn-primary" disabled={isPending}>
              {isPending ? 'שומר...' : creating ? 'צור לקוח' : 'שמור שינויים'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
